import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "@/types";
import {
  api,
  clearTokens,
  endImpersonatorSession,
  getAccessToken,
  isImpersonating,
  setTokens,
  startImpersonatorSession,
} from "@/lib/api";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  impersonate: (userId: string) => Promise<User>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const refreshUser = async () => {
    if (!getAccessToken()) {
      setUser(null);
      setImpersonating(false);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<User>("/api/v1.0/auth/me");
      setUser(me);
      setImpersonating(isImpersonating());
    } catch {
      // If the (short-lived) impersonation token died, fall back to the
      // parked admin session instead of logging the admin out entirely.
      if (endImpersonatorSession()) {
        await refreshUser();
        return;
      }
      setUser(null);
      setImpersonating(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const res = await api.post<{ user: User; access_token: string; refresh_token: string }>(
      "/api/v1.0/auth/login",
      { email, password },
    );
    setTokens(res.access_token, res.refresh_token);
    setUser(res.user);
    setImpersonating(false);
  };

  const register = async (email: string, password: string, fullName: string) => {
    const res = await api.post<{ user: User; access_token: string; refresh_token: string }>(
      "/api/v1.0/auth/register",
      { email, password, full_name: fullName },
    );
    setTokens(res.access_token, res.refresh_token);
    setUser(res.user);
    setImpersonating(false);
  };

  const logout = async () => {
    try {
      await api.post("/api/v1.0/auth/logout");
    } finally {
      clearTokens();
      setUser(null);
      setImpersonating(false);
    }
  };

  const impersonate = async (userId: string) => {
    const res = await api.post<{ user: User; access_token: string }>(`/api/v1.0/users/${userId}/impersonate`);
    startImpersonatorSession(res.access_token);
    setUser(res.user);
    setImpersonating(true);
    return res.user;
  };

  const stopImpersonating = async () => {
    if (!endImpersonatorSession()) return;
    await refreshUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isImpersonating: impersonating,
        login,
        register,
        logout,
        refreshUser,
        impersonate,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
